import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '@/api/http';
import { authApi, sessionsApi } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { RescheduleModal } from '@/components/shared/reschedule-modal';
import { ReviewForm, ReviewDisplay } from '@/components/shared/review-form';
import { PresentialRecorder } from '@/components/shared/presential-recorder';
import { TranscriptCard } from '@/components/shared/transcript-card';
import { BillingModeBadge } from '@/components/shared/billing-mode-badge';
import { ClientNfseButton } from '@/components/shared/client-nfse-button';
import { MeetJoinButton } from '@/components/shared/meet-join-button';
import { CancelSessionModal } from '@/components/shared/cancel-session-modal';
import {
  canCancel, canConfirm, canEditNote, canMarkDone, canReplyReview, canReschedule, canReview,
  shouldShowPaymentBanner,
  type ActorRole,
} from '@/lib/business-rules';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/sessoes/$id')({
  // ?reschedule=1 abre o modal automaticamente (vindo do magic-link da
  // confirmação WhatsApp do cliente).
  validateSearch: (s: Record<string, unknown>) => ({ reschedule: s.reschedule ? 1 : undefined }),
  component: SessionDetailPage,
});

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Aguardando', CONFIRMED: 'Confirmada', DONE: 'Concluída',
  DISPUTED: 'Em revisão', CANCELLED: 'Cancelada',
};
const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED: 'bg-teal-50 text-teal-700 border-teal-200',
  DONE: 'bg-slate-100 text-slate-600 border-slate-200',
  DISPUTED: 'bg-orange-50 text-orange-700 border-orange-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};

const money = (v: any) => `R$ ${Number(v ?? 0).toFixed(2).replace('.', ',')}`;

function SessionDetailPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const [showCancel, setShowCancel] = useState(false);
  const [showReschedule, setShowReschedule] = useState(!!search.reschedule);
  // reason + cancel mutation vivem agora dentro de CancelSessionModal (FB-9).

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const actorRole: ActorRole = me?.therapistProfile ? 'THERAPIST' : me?.clientProfile ? 'CLIENT' : 'ADMIN';

  const { data, isLoading } = useQuery({
    queryKey: ['session', id],
    queryFn: () => api.get(`sessions/${id}`).json<any>(),
  });

  const confirm = useMutation({
    mutationFn: () => api.patch(`sessions/${id}/confirm`).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session', id] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
  const markDone = useMutation({
    mutationFn: () => api.patch(`sessions/${id}/done`).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session', id] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
  if (isLoading) return <p className="text-slate-500 text-sm">Carregando...</p>;
  if (!data) return (
    <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
      <p className="text-slate-500">Sessão não encontrada.</p>
    </div>
  );

  const peer = actorRole === 'THERAPIST' ? data.client : data.therapist;
  const peerUser = peer?.user;
  const peerLabel = actorRole === 'THERAPIST' ? 'CLIENTE' : 'TERAPEUTA';

  // Computa elegibilidades (regras de negócio)
  const rescheduleEligibility = canReschedule({
    status: data.status, rescheduleCount: data.rescheduleCount ?? 0,
    currentScheduledAt: data.scheduledAt, actorRole,
  });
  const cancelEligibility = canCancel({
    status: data.status, paymentStatus: data.payment?.status,
    isRecurringSession: !!data.recurringScheduleId, actorRole,
  });
  const showConfirm = canConfirm({ status: data.status, actorRole });
  const showMarkDone = canMarkDone({ status: data.status, scheduledAt: data.scheduledAt, actorRole });
  const showNote = canEditNote({ status: data.status, actorRole });
  const showReview = canReview({ status: data.status, actorRole, alreadyReviewed: !!data.review });
  const replyAllowed = canReplyReview({
    actorRole, hasReview: !!data.review, alreadyReplied: !!data.review?.therapistReply,
  });
  const showPayBanner = shouldShowPaymentBanner({ actorRole, paymentStatus: data.payment?.status });

  return (
    <div className="space-y-5">
      <div>
        <Link to="/dashboard/sessoes" className="text-sm text-slate-500 hover:text-slate-700">
          ← Voltar
        </Link>
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-2xl font-extrabold text-slate-900">Detalhes da sessão</h1>
          <div className="flex items-center gap-2">
            <BillingModeBadge mode={data.billingMode} />
            <span className={cn('text-xs px-3 py-1 rounded-full border', STATUS_STYLES[data.status] ?? 'bg-slate-100 text-slate-600 border-slate-200')}>
              {STATUS_LABELS[data.status] ?? data.status}
            </span>
          </div>
        </div>
      </div>

      {/* Banner de pagamento pendente para o cliente (P-14) */}
      {showPayBanner && data.payment && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3 flex-wrap">
          <span className="text-xl">⚠️</span>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-800 text-sm">Pagamento pendente</p>
            <p className="text-xs text-amber-700">
              Pague para confirmar sua sessão · {money(data.payment.amount)}
            </p>
          </div>
          <Link to="/pagamento/$paymentId" params={{ paymentId: data.payment.id }}>
            <Button className="bg-amber-500 hover:bg-amber-600 text-white">Pagar agora →</Button>
          </Link>
        </div>
      )}

      {/* Card cliente + dados */}
      <section className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        <div className="flex items-start gap-5">
          {(peer?.avatarUrl || peerUser?.image) ? (
            <img src={peer?.avatarUrl ?? peerUser.image} alt="" className="size-16 rounded-full object-cover shrink-0" />
          ) : (
            <div className="size-16 rounded-full bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white font-bold text-2xl shrink-0">
              {(peerUser?.name ?? '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{peerLabel}</p>
            <p className="text-xl font-bold text-slate-800 mt-0.5">{peerUser?.name ?? '—'}</p>
            {peer?.phone && <p className="text-sm text-slate-500">{peer.phone}</p>}
            {(peer?.city || peer?.state) && (
              <p className="text-sm text-slate-500">{[peer.city, peer.state].filter(Boolean).join(', ')}</p>
            )}
            <p className="text-xs text-slate-400 italic mt-1">Dados protegidos conforme LGPD</p>
          </div>
        </div>

        <hr className="border-slate-100" />

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          <Info label="Data e horário" value={
            new Date(data.scheduledAt).toLocaleString('pt-BR', {
              weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            }).replace(',', ' às')
          } />
          <Info label="Modalidade" value={
            data.modality === 'ONLINE' ? 'Online — Google Meet'
              : data.modality === 'PRESENCIAL' ? 'Presencial' : 'Híbrido'
          } />
          <Info label="Duração prevista" value={`${data.durationMin} minutos`} />
          <Info label="Valor (S-04)" value={money(data.priceCharged)} />
        </div>

        {data.meetLink && data.status !== 'CANCELLED' && data.status !== 'DONE' && (
          <>
            <hr className="border-slate-100" />
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Link da sessão</p>
              <MeetJoinButton
                meetLink={data.meetLink}
                transcriptionEnabled={!!data.therapist?.transcriptionEnabled}
                recordingConsent={!!data.client?.recordingConsent}
                actorRole={actorRole}
              />
            </div>
          </>
        )}

        {data.status === 'CANCELLED' && data.cancellationReason && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
            <strong>Cancelada {data.cancelledBy ? `por ${data.cancelledBy === 'THERAPIST' ? 'profissional' : 'cliente'}` : ''}:</strong>{' '}
            {data.cancellationReason}
          </div>
        )}

        {data.status === 'CANCELLED' && data.payment?.refundStatus && data.payment.refundStatus !== 'NONE' && (
          <RefundStatusCard payment={data.payment} isClient={actorRole === 'CLIENT'} />
        )}

        {/* Histórico de reagendamentos (S-09) */}
        {data.reschedules?.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
              Histórico de reagendamentos ({data.reschedules.length})
            </summary>
            <ul className="mt-2 space-y-1.5 text-xs text-slate-500">
              {data.reschedules.map((r: any) => (
                <li key={r.id}>
                  {new Date(r.createdAt).toLocaleDateString('pt-BR')} · {r.actorRole === 'THERAPIST' ? 'Profissional' : 'Cliente'}:{' '}
                  {new Date(r.oldStartAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} →{' '}
                  {new Date(r.newStartAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {r.reason && <span className="italic"> · {r.reason}</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* Pagamento */}
      {data.payment && (
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Pagamento</h2>
          <div className="grid sm:grid-cols-4 gap-x-6 gap-y-3">
            <Info label="Status" value={
              data.payment.status === 'PAID' ? 'Pago'
                : data.payment.status === 'PENDING' ? 'Pendente'
                : data.payment.status === 'REFUNDED' ? 'Reembolsado'
                : data.payment.status
            } />
            <Info label="Método" value={data.payment.method ?? '—'} />
            <Info label="Valor" value={money(data.payment.amount)} />
            <Info
              label={data.payment.status === 'PAID' ? 'Pago em' : data.payment.status === 'REFUNDED' ? 'Reembolsado em' : 'Criado em'}
              value={new Date(data.payment.paidAt ?? data.payment.refundedAt ?? data.payment.createdAt).toLocaleString('pt-BR', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            />
          </div>
          {/* F2/F5 — botão opt-in NFSe (só pra cliente) */}
          {actorRole === 'CLIENT' && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <p className="text-slate-700 font-semibold">Nota fiscal</p>
                <p className="text-xs text-slate-500">Para reembolso de convênio ou IRPF.</p>
              </div>
              <ClientNfseButton
                paymentId={data.payment.id}
                paymentStatus={data.payment.status}
                sessionStatus={data.status}
                professionalCategory={data.therapist?.professionalCategory}
                clientNfseStatus={data.payment.clientNfseStatus ?? 'NONE'}
                invalidateKeys={[['session', id]]}
              />
            </div>
          )}
        </section>
      )}

      {/* Validação Meet (S-10) */}
      {actorRole === 'THERAPIST' && data.modality === 'ONLINE' && (data.status === 'CONFIRMED' || data.status === 'DONE') && (
        <MeetValidationSection
          sessionId={id}
          meetLink={data.meetLink}
          therapistJoined={data.meetTherapistJoined}
          clientJoined={data.meetClientJoined}
          actualDurationMin={data.meetActualDurationMin}
          showMarkDone={!!showMarkDone}
          onMarkDone={() => markDone.mutate()}
          markDonePending={markDone.isPending}
          transcriptionEnabled={!!data.therapist?.transcriptionEnabled}
          recordingConsent={!!data.client?.recordingConsent}
        />
      )}

      {/* Confirmar (S-02) */}
      {showConfirm && (
        <section className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-amber-800">Esta sessão ainda precisa de confirmação.</p>
          <Button onClick={() => confirm.mutate()} disabled={confirm.isPending} className="bg-amber-500 hover:bg-amber-600 text-white">
            {confirm.isPending ? 'Confirmando...' : 'Confirmar sessão'}
          </Button>
        </section>
      )}

      {/* T-05 — Gravação presencial (só terapeuta, sessão presencial/híbrida ativa).
          O componente sonda /transcription/status e mostra estado correto se
          dual opt-in não estiver satisfeito. */}
      {actorRole === 'THERAPIST'
        && (data.modality === 'PRESENCIAL' || data.modality === 'HYBRID')
        && data.status !== 'CANCELLED'
        && data.status !== 'DISPUTED' && (
        <PresentialRecorder sessionId={id} />
      )}

      {/* T-09 — Transcrição da sessão (só terapeuta vê; componente sonda e
          decide sozinho se renderiza algo — esconde se ainda não existe). */}
      {actorRole === 'THERAPIST' && <TranscriptCard sessionId={id} />}

      {/* Prontuário (S-11) */}
      {showNote && <NoteSection sessionId={id} note={data.sessionNote} />}

      {/* Review form (AV-01..AV-04) — cliente após DONE sem review */}
      {showReview && (
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Avalie a sessão</h2>
          <p className="text-xs text-slate-500 mb-4">Sua avaliação ajuda outros clientes a escolherem.</p>
          <ReviewForm sessionId={id} />
        </section>
      )}

      {/* Review existente (com possível resposta do terapeuta — AV-07) */}
      {data.review && (
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Avaliação</h2>
          <ReviewDisplay review={data.review} canReply={replyAllowed} sessionId={id} />
        </section>
      )}

      {/* Ações: Reagendar + Cancelar */}
      {(rescheduleEligibility.ok || cancelEligibility.ok) && (
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Ações</h2>
          <p className="text-xs text-slate-500 mb-4">
            Reagendamento: máx 2 por sessão · cliente exige 3h de antecedência
          </p>
          <div className="flex gap-2 flex-wrap">
            {rescheduleEligibility.ok ? (
              <Button variant="outline" onClick={() => setShowReschedule(true)}>
                Reagendar
              </Button>
            ) : (
              <span
                className="text-xs text-slate-400 italic px-3 py-2"
                title={rescheduleEligibility.reason}
              >
                Reagendamento indisponível
              </span>
            )}
            {cancelEligibility.ok ? (
              <Button
                variant="outline"
                onClick={() => setShowCancel((v) => !v)}
                className="text-red-500 border-red-200 hover:bg-red-50"
              >
                Cancelar sessão
              </Button>
            ) : (
              <span
                className="text-xs text-slate-400 italic px-3 py-2"
                title={cancelEligibility.reason}
              >
                Cancelamento indisponível
              </span>
            )}
          </div>

        </section>
      )}

      {/* FB-9 — modal de cancelamento com preview de reembolso */}
      {showCancel && cancelEligibility.ok && (
        <CancelSessionModal
          sessionId={id}
          paymentStatus={data.payment?.status}
          paymentMethod={data.payment?.method}
          paymentAmount={Number(data.payment?.amount ?? 0)}
          isBatchPackage={!!data.payment?.recurringScheduleId}
          onClose={() => setShowCancel(false)}
        />
      )}

      {/* Modal de reagendamento */}
      {showReschedule && (
        <RescheduleModal
          sessionId={id}
          therapistId={data.therapist.id}
          status={data.status}
          rescheduleCount={data.rescheduleCount ?? 0}
          currentScheduledAt={data.scheduledAt}
          actorRole={actorRole}
          onClose={() => setShowReschedule(false)}
        />
      )}
    </div>
  );
}

/* ─── PRONTUÁRIO (S-11) ───────────────────────────────────────────────────── */

function NoteSection({ sessionId, note }: { sessionId: string; note: any }) {
  const qc = useQueryClient();
  const { register, handleSubmit, reset } = useForm<{ chiefComplaint: string; evolution: string; plan: string }>();

  useEffect(() => {
    reset({
      chiefComplaint: note?.chiefComplaint ?? '',
      evolution: note?.evolution ?? '',
      plan: note?.plan ?? '',
    });
  }, [note, reset]);

  const save = useMutation({
    mutationFn: (body: any) => api.post(`sessions/${sessionId}/note`, { json: body }).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', sessionId] }),
  });

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-lg font-bold text-slate-800">Prontuário</h2>
      <p className="text-xs text-slate-500 mb-4">Visível apenas para você (S-11)</p>
      <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
        <Field label="Queixa principal">
          <input {...register('chiefComplaint')} className="input" />
        </Field>
        <Field label="Evolução *">
          <textarea {...register('evolution', { required: true })} rows={5} className="input min-h-32 py-2" />
        </Field>
        <Field label="Plano terapêutico">
          <textarea {...register('plan')} rows={3} className="input min-h-20 py-2" />
        </Field>
        {save.isSuccess && <p className="text-sm text-teal-600">Prontuário salvo.</p>}
        <Button type="submit" disabled={save.isPending} className="bg-teal-500 hover:bg-teal-600 text-white">
          {save.isPending ? 'Salvando...' : 'Salvar prontuário'}
        </Button>
      </form>
      <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:38px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
    </section>
  );
}

/* ─── VALIDAÇÃO MEET (V-01..V-05) ──────────────────────────────────────────── */

function MeetValidationSection({
  sessionId,
  meetLink,
  therapistJoined,
  clientJoined,
  actualDurationMin,
  showMarkDone,
  onMarkDone,
  markDonePending,
  transcriptionEnabled,
  recordingConsent,
}: {
  sessionId: string;
  meetLink: string | null | undefined;
  therapistJoined: boolean | null;
  clientJoined: boolean | null;
  actualDurationMin: number | null;
  showMarkDone: boolean;
  onMarkDone: () => void;
  markDonePending: boolean;
  transcriptionEnabled: boolean;
  recordingConsent: boolean;
}) {
  const qc = useQueryClient();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const sync = useMutation({
    mutationFn: () => api.post(`sessions/${sessionId}/sync-meet-details`).json<any>(),
    onSuccess: () => {
      setErrorMsg(null);
      qc.invalidateQueries({ queryKey: ['session', sessionId] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
    },
    onError: async (err: any) => {
      let msg = 'Falha ao consultar Meet.';
      try {
        const body = await err?.response?.json?.();
        if (body?.message) msg = Array.isArray(body.message) ? body.message.join(' · ') : body.message;
      } catch { /* noop */ }
      setErrorMsg(msg);
    },
  });

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <h2 className="text-lg font-bold text-slate-800 mb-1">Validação da chamada</h2>
      <p className="text-xs text-slate-500 mb-4">
        Valide quem entrou e a duração efetiva (Google Meet API). Dados pós-chamada
        podem levar alguns minutos para ficar disponíveis.
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="bg-indigo-500 hover:bg-indigo-600 text-white"
        >
          {sync.isPending ? 'Consultando...' : 'Validar presença na chamada'}
        </Button>
        {meetLink && (
          <MeetJoinButton
            meetLink={meetLink}
            transcriptionEnabled={transcriptionEnabled}
            recordingConsent={recordingConsent}
            actorRole="THERAPIST"
            className="inline-flex border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2 rounded-md text-sm font-medium transition"
            label="Entrar no Google Meet"
          />
        )}
        {showMarkDone && (
          <Button variant="outline" onClick={onMarkDone} disabled={markDonePending}>
            {markDonePending ? 'Salvando...' : 'Marcar como concluída'}
          </Button>
        )}
      </div>

      {errorMsg && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {errorMsg}
        </div>
      )}

      {(therapistJoined !== null || clientJoined !== null) && (
        <div className="mt-4 grid sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
          <Info label="Profissional entrou" value={therapistJoined ? 'Sim' : 'Não'} />
          <Info label="Cliente entrou" value={clientJoined ? 'Sim' : 'Não'} />
          <Info label="Duração efetiva" value={actualDurationMin != null ? `${actualDurationMin} min` : '—'} />
        </div>
      )}
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
      <p className="text-sm text-slate-800 mt-1 whitespace-pre-line">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

/**
 * Card de status do reembolso, mostrado no detalhe da sessão CANCELLED
 * quando existe fluxo de refund em andamento ou concluído. Cliente vê
 * mensagem tranquilizadora + prazo de 7 dias úteis; profissional vê
 * status neutro só pra saber que existe reembolso em curso.
 */
function RefundStatusCard({ payment, isClient }: { payment: any; isClient: boolean }) {
  const status: 'REQUESTED' | 'PROCESSING' | 'DONE' | 'FAILED' = payment.refundStatus;

  const meta = {
    REQUESTED: {
      title: '⏳ Reembolso solicitado',
      body: isClient
        ? 'Seu reembolso foi solicitado. Enviaremos o PIX na chave que você informou em até 7 dias úteis.'
        : 'A cliente solicitou reembolso desta sessão. A plataforma está processando o PIX.',
      cls: 'border-amber-200 bg-amber-50 text-amber-900',
    },
    PROCESSING: {
      title: '⚙ Reembolso em processamento',
      body: 'O PIX está sendo enviado agora. Deve ser refletido em instantes.',
      cls: 'border-indigo-200 bg-indigo-50 text-indigo-900',
    },
    DONE: {
      title: '✓ Reembolso concluído',
      body: isClient
        ? `O reembolso de ${money(payment.refundAmount)} foi enviado por PIX na chave ${payment.refundPixKey ?? '—'}. Se não aparecer na sua conta, entre em contato conosco.`
        : `A cliente foi reembolsada em ${new Date(payment.refundedAt ?? Date.now()).toLocaleDateString('pt-BR')}.`,
      cls: 'border-teal-200 bg-teal-50 text-teal-900',
    },
    FAILED: {
      title: '⚠ Reembolso com falha técnica',
      body: isClient
        ? 'Houve uma falha ao enviar o PIX automaticamente. Já fomos notificados e vamos reprocessar manualmente. Se demorar mais de 2 dias úteis, escreva pra gente.'
        : 'Falha no envio automático. O admin será notificado pra reprocessar.',
      cls: 'border-red-200 bg-red-50 text-red-900',
    },
  }[status];

  if (!meta) return null;

  return (
    <div className={cn('rounded-md border p-4 text-sm', meta.cls)}>
      <p className="font-semibold">{meta.title}</p>
      <p className="mt-1">{meta.body}</p>
      {status === 'DONE' && payment.refundInterEndToEndId && (
        <p className="mt-2 text-[11px] text-slate-500 font-mono break-all">
          E2E: {payment.refundInterEndToEndId}
        </p>
      )}
    </div>
  );
}
